<template>
    <transition
        mode="out-in"
        name="fade">
        <section
            class="events"
            key="events"
            v-if="!isLoadingEvents && events.length > 0">
            <ul>
                <li
                    class="event"
                    v-for="event in events"
                    :key="event.id">
                    <router-link
                        class="event__link"
                        :class="{ 'event__link--urgent': Boolean(eventsUrgencyMap[event.id]) }"
                        :to="makeEventRoute(event)"
                        @click.native="onEventClick(event)">
                        <script type="application/ld+json">
                        {
                            "@context": "http://schema.org",
                            "@type": "Event",
                            "name": "{{ event.name }}",
                            "location": {
                                "@type": "Place",
                                "name": "{{ event.venueName }}",
                                "address": {
                                    "@type": "PostalAddress",
                                    "addressLocality": "{{ event.venueCity }}",
                                    "addressRegion": "{{ event.venueRegionCode }}"
                                }
                            },
                            "startDate": "{{ event.localDate }}",
                            "url": "{{ event.schemaOrgUrl }}"
                        }
                        </script>
                        <div
                            class="event__thumbnail"
                            v-if="isShowThumbnails"
                            :style="{ backgroundImage: `url(${thumbnailImage(event)})` }">
                            <Tag
                                class="event__sold-out--desktop"
                                v-if="event.is_sold_out"
                                context="error"
                                text="Sold Out" />
                        </div>
                        <div
                            class="fx-hidden--small event__listing-date"
                            v-else>
                            <fx-event-listing-date :event="{ local_date: event.localDate, is_date_tbd: event.isDateTbd, is_time_tbd: event.isTimeTbd }"></fx-event-listing-date>
                        </div>
                        <div class="event__info">
                            <div>
                                <div class="event__name">{{ event.name }}</div>
                                <div class="event__content">
                                    <div class="left">
                                        <div
                                            class="event__datetime"
                                            :class="{ 'fx-hidden--medium': !isShowThumbnails, 'fx-hidden--large': !isShowThumbnails, 'fx-hidden--full': !isShowThumbnails }">
                                            <template v-if="event.isDateTbd"><span class="event__date">Date TBD</span></template>
                                            <template v-else-if="event.isTimeTbd"><span class="event__date">{{ event.localDate | date('ddd, MMM D, YYYY') }}</span>, Time TBD</template>
                                            <template v-else-if="event.localDateLabel"><span class="event__date">{{ event.localDateLabel }}</span> at {{ event.localDate | date('h:mma') }}</template>
                                            <template v-else><span class="event__date">{{ event.localDate | date('ddd, MMM D, YYYY') }}</span> at {{ event.localDate | date('h:mma') }}</template>
                                        </div>
                                        <div>
                                            <span class="event__location">
                                                <span v-if="event.venueRegionCode">{{ event.venueName }}, {{ event.venueCity }}, {{ event.venueRegionCode }}</span>
                                                <span v-else>{{ event.venueName }}, {{ event.venueCity }}</span>
                                            </span>
                                            <span class="event__price">
                                                <i
                                                    v-if="!event.is_sold_out"
                                                    class="event__price-dot"
                                                >
                                                    &middot;
                                                </i>
                                                <Tag
                                                    v-if="event.is_sold_out"
                                                    class="event__sold-out--mobile"
                                                    context="error"
                                                    text="Sold Out" />
                                                <template v-else-if="!$config.isIntegrated">
                                                    <span v-if="isLoyaltyPricing && event.pointsPriceMin"> from {{ formatPoints(event.pointsPriceMin, event.pointsCurrency) }}</span>
                                                    <span v-else> from {{ event.moneyPriceMin | numeral('$0,0') }}</span>
                                                </template>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="right">
                                        <span class="fx-hidden--medium fx-hidden--large fx-hidden--full icon-chevron-lg-right"></span>
                                    </div>
                                </div>
                            </div>
                            <div v-if="event.isPartnerExclusive">
                                <Tag text="Cardholder Exclusive"
                                    context="exclusive"
                                />
                            </div>
                        </div>
                        <span
                            class="event__urgency"
                            v-if="eventsUrgencyMap[event.id]">{{ $t('event-list.urgency', { count: eventsUrgencyMap[event.id] }) }}</span>
                        <span
                            class="button fx-hidden--small fx-hidden--medium fx-hidden--large event__get-tickets"
                            :class="{ 'event__get-tickets--urgent': Boolean(eventsUrgencyMap[event.id]) }">{{ event.is_sold_out ? 'View Details': 'Get Tickets' }}</span>
                    </router-link>
                </li>
            </ul>
            <button
                class="button load-more"
                type="button"
                v-if="isShowLoadMoreButton"
                @click="loadMoreEvents">{{ isLoadingMoreEvents ? loadingMoreCopy : loadMoreCopy }}</button>
        </section>
        <section
            class="loading"
            key="loading"
            v-else-if="isLoadingEvents">
            <fx-spinner></fx-spinner>
        </section>
        <slot
            name="events-not-found"
            v-else
        >
            <NoResults
                :show-redirect="isShowRedirect"
            />
        </slot>
    </transition>
</template>

<script>
import fxEventListingDate from 'core/components/EventListingDateComponent.vue'
import fxSpinner from 'core/components/SpinnerComponent.vue'
import Tag from 'core/components/TagComponent.vue'
import NoResults from '../components/NoResults.vue'
import resizable from '../mixins/Resizable'

export default {
    components: {
        fxEventListingDate,
        fxSpinner,
        NoResults,
        Tag
    },
    computed: {
        isShowRedirect () {
            return (this.$route.name === 'search' || !this.filtersApplied) && this.$route.name !== 'home'
        },
        isShowLoadMoreButton () {
            return this.events.length > 0 && this.pagination.current_page < this.pagination.last_page
        }
    },
    methods: {
        loadMoreEvents () {
            this.$emit('load-more-events')
        },
        thumbnailImage (event) {
            return this.isWindowSmall ? (event.imageUrlRectangle || event.imageUrlSquare) : (event.imageUrlSquare || event.imageUrlRectangle)
        }
    },
    props: {
        events: Array,
        eventsUrgencyMap: {
            default () {
                return {}
            },
            type: Object
        },
        filtersApplied: {
            default: false,
            required: false,
            type: Boolean
        },
        formatPoints: {
            default (pointsAmount) {
                return pointsAmount
            },
            required: false,
            type: Function
        },
        isLoadingEvents: Boolean,
        isLoadingMoreEvents: Boolean,
        isLoyaltyPricing: {
            default: false,
            required: false,
            type: Boolean
        },
        isShowThumbnails: {
            default: true,
            type: Boolean
        },
        loadMoreCopy: String,
        loadingMoreCopy: String,
        makeEventRoute: {
            default (event) {
                return ''
            },
            required: false,
            type: Function
        },
        onEventClick: {
            default: () => {},
            type: Function
        },
        pagination: Object
    },
    mixins: [resizable]
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.button {
    background-color: transparent;
    border: var(--c-primary) solid 1px;
    border-radius: 20px;
    color: var(--c-primary);
    padding: 6px 16px;
    transition: 0.2s ease;
    white-space: nowrap;

    &:hover,
    &:focus {
        background-color: var(--c-primary);
        color: white;
        cursor: pointer;
        transition: 0.2s ease;
    }
}

.event {
    margin-bottom: 8px;
    -webkit-font-smoothing: antialiased;

    & .event__date {
        color: var(--c-gray-1);
    }

    & .event__datetime {
        margin: 8px auto 0;
    }

    & .event__get-tickets {
        background-color: var(--c-primary);
        border-radius: 2px;
        color: #fff;
        display: block;
        font-size: var(--fs-large);
        height: 32px;
        line-height: 18px;
        margin-left: 20px;
        margin-right: 24px;
        text-align: center;
        width: 118px;

        &:hover,
        &:focus {
            background: color(var(--c-primary) shade(10%));
        }

        &:active {
            background: color(var(--c-primary) shade(20%));
        }

        &.event__get-tickets--urgent {
            margin-left: 40px;
        }
    }

    & .event__info {
        color: var(--c-gray-4);
        display: flex;
        flex-direction: column;
        font-weight: var(--fw-light);
        margin-right: auto;
        padding: 25px;

        @media (--mq-small-max) {
            flex-direction: column-reverse;
            padding: 8px;
            width: 100%;
        }
    }

    & .event__content {
        color: var(--c-gray-5);
        display: flex;

        & .right {
            align-self: center;
            flex: 1 1 auto;
            margin: 0 8px;
            text-align: right;

            & .icon-chevron-lg-right {
                color: var(--c-primary);
                font-size: var(--fs-large);
            }
        }
    }

    & .event__link {
        align-items: center;
        background-color: white;
        border-radius: 5px;
        border: 1px solid var(--c-gray-9);
        display: flex;
        flex-direction: column;
        padding: 0;
        position: relative;
        text-decoration: none;
        transition: 0.2s ease;

        @media (--mq-medium-min) {
            flex-direction: row;
            height: 116px;
        }

        &.event__link--urgent {
            @media (--mq-full-min) {
                min-height: 116px;
            }
        }

        &:focus,
        &:hover {
            box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.25);
            outline: none;
            transform: scale(1.01);
            transition: 0.2s ease;
        }
    }

    & .event__listing-date {
        margin-left: 30px;
        margin-right: 20px;
    }

    & .event__location {
        font-size: var(--fs-normal);
        margin-bottom: 4px;
    }

    & .event__name {
        color: var(--c-gray-1);
        font-size: var(--fs-large);
        font-weight: var(--fw-bold);
        letter-spacing: 0;
        line-height: 20px;
    }

    & .event__price {
        color: var(--c-accent);
        text-transform: lowercase;

        @media (--mq-small-max) {
            display: block;
        }

        & .event__price-dot {
            color: var(--c-gray-4);
            font-weight: var(--fw-light);

            @media (--mq-large-min) {
                display: none;
            }
        }

        & .event__sold-out--mobile {
            @media (--mq-large-min) {
                display: none;
            }
        }
    }

    & .event__thumbnail {
        background-color: var(--c-primary);
        background-position: 50% 50%;
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: 5px 5px 0 0;
        flex-shrink: 0;
        height: 165px;
        width: 100%;

        @media (--mq-medium-min) {
            display: flex;
            height: 100%;
            width: 116px;
            border-radius: 5px 0 0 5px;
        }

        & >>> .event__sold-out--desktop {
            display: none;
            @media (--mq-large-min) {
                display: block;
                height: 15px;
                margin: auto;
                width: 100%;
            }
        }
    }

    & .event__urgency {
        color: #f44;
        display: block;
        font-size: var(--fs-normal);
        position: absolute;
        right: 20px;
        top: 21px;

        @media (--mq-large-max) {
            bottom: 20px;
            font-size: var(--fs-small);
            margin-bottom: 1px;
            top: auto;
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 1s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

>>> .fx-event-listing-date-component {
    & .date-row {
        font-size: var(--fs-small);
        font-weight: normal;

        &:nth-child(2) {
            font-size: var(--fs-normal);
        }
    }
}

.load-more {
    background: white;
    border-radius: 2px;
    display: block;
    font-size: 14px;
    height: 36px;
    margin: 20px auto 0;
    max-width: 660px;
    outline: 0;
    padding: 10px 0;
    width: 100%;
    &:hover {
        background: #F0F0F0;
        color: var(--c-primary);
    }
    &:active {
        background: color(var(--c-primary) shade(20%));
        color: white;
    }
}

.loading {
    height: 400px;
    position: relative;
    width: 100%
}
</style>
